import { Badge, Button, Card, CardBody, CardHeader, EmptyState, FormField, Modal, Spinner } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PickingFlow } from '@warehouse/shared';
import { Plus, Route, Star, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../../components/PageHeader';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { flowsApi, pickingKeys } from '../api';
import { FlowStepEditor, type FlowStepDraft } from '../components/FlowStepEditor';

/**
 * Picking flow configuration (spec §5.8). One flow is the default; every round
 * built without an explicit flow inherits it, and the flow's order is what both
 * the scanner and the printed pick list sort by.
 */
export function PickingFlowsPage(): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const { t: tc } = useTranslation('common');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<PickingFlow | 'new' | null>(null);

  const flows = useQuery({ queryKey: pickingKeys.flows, queryFn: flowsApi.list });
  useLiveQuery(['warehouse'], pickingKeys.flows);

  const setDefault = useMutation({
    mutationFn: flowsApi.setDefault,
    onSuccess: async () => {
      toastSuccess(t('flows.defaultSet'));
      await queryClient.invalidateQueries({ queryKey: pickingKeys.flows });
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const remove = useMutation({
    mutationFn: flowsApi.remove,
    onSuccess: async () => {
      toastSuccess(t('flows.deleted'));
      await queryClient.invalidateQueries({ queryKey: pickingKeys.flows });
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <>
      <PageHeader
        title={t('flows.title')}
        subtitle={t('flows.subtitle')}
        actions={
          <Button
            icon={<Plus className="size-4" />}
            onClick={() => {
              setEditing('new');
            }}
          >
            {t('flows.new')}
          </Button>
        }
      />

      {flows.isLoading ? <Spinner /> : null}

      {!flows.isLoading && (flows.data?.length ?? 0) === 0 ? (
        <EmptyState
          icon={<Route />}
          title={t('flows.empty')}
          description={t('flows.emptyDescription')}
          action={
            <Button
              onClick={() => {
                setEditing('new');
              }}
            >
              {t('flows.new')}
            </Button>
          }
        />
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {flows.data?.map((flow) => (
          <Card key={flow.id}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <h2 className="font-display text-base font-semibold text-ink">{flow.name}</h2>
                {flow.isDefault ? (
                  <Badge tone="brand" size="sm">
                    {t('flows.defaultBadge')}
                  </Badge>
                ) : null}
              </div>
            </CardHeader>
            <CardBody>
              <p className="mb-3 text-sm text-ink-muted">
                {flow.steps.length > 0
                  ? t('flows.stepCount', { count: flow.steps.length })
                  : t('flows.emptySteps')}
              </p>
              <ol className="mb-4 flex flex-wrap gap-1.5">
                {flow.steps.slice(0, 8).map((step) => (
                  <li key={step.id}>
                    <Badge tone="neutral" size="sm">
                      {step.label}
                    </Badge>
                  </li>
                ))}
                {flow.steps.length > 8 ? (
                  <li>
                    <Badge tone="neutral" size="sm">
                      +{flow.steps.length - 8}
                    </Badge>
                  </li>
                ) : null}
              </ol>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setEditing(flow);
                  }}
                >
                  {t('flows.edit')}
                </Button>
                {!flow.isDefault ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Star className="size-3.5" />}
                      loading={setDefault.isPending}
                      onClick={() => {
                        setDefault.mutate(flow.id);
                      }}
                    >
                      {t('flows.setDefault')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Trash2 className="size-3.5" />}
                      onClick={() => {
                        if (globalThis.confirm(t('flows.deleteConfirm', { name: flow.name }))) {
                          remove.mutate(flow.id);
                        }
                      }}
                    >
                      {tc('actions.delete')}
                    </Button>
                  </>
                ) : null}
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {editing ? (
        <FlowEditorModal
          flow={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
    </>
  );
}

function FlowEditorModal({ flow, onClose }: { flow: PickingFlow | null; onClose: () => void }): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const { t: tc } = useTranslation('common');
  const queryClient = useQueryClient();

  const [name, setName] = useState(flow?.name ?? '');
  const [isDefault, setIsDefault] = useState(flow?.isDefault ?? false);
  const [steps, setSteps] = useState<FlowStepDraft[]>(
    (flow?.steps ?? []).map((step) => ({
      id: step.binId ?? step.shelfId ?? step.areaId ?? step.id,
      kind: step.kind,
      label: step.label,
    })),
  );

  const targets = useQuery({ queryKey: pickingKeys.flowTargets, queryFn: flowsApi.targets });

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(),
        isDefault,
        steps: steps.map((step) => ({
          areaId: step.kind === 'area' ? step.id : null,
          shelfId: step.kind === 'shelf' ? step.id : null,
          binId: step.kind === 'bin' ? step.id : null,
        })),
      };
      return flow ? flowsApi.update(flow.id, payload) : flowsApi.create(payload);
    },
    onSuccess: async () => {
      toastSuccess(flow ? t('flows.updated') : t('flows.created'));
      await queryClient.invalidateQueries({ queryKey: pickingKeys.flows });
      onClose();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <Modal open onClose={onClose} title={flow ? t('flows.edit') : t('flows.new')} width={900}>
      <div className="flex flex-col gap-5">
        <FormField
          label={t('flows.name')}
          value={name}
          placeholder={t('flows.namePlaceholder')}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(event) => {
              setIsDefault(event.target.checked);
            }}
          />
          {t('flows.isDefault')}
        </label>

        <FlowStepEditor
          steps={steps}
          onChange={setSteps}
          targets={targets.data ?? []}
          loadingTargets={targets.isLoading}
        />

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button
            loading={save.isPending}
            disabled={name.trim().length === 0}
            onClick={() => {
              save.mutate();
            }}
          >
            {tc('actions.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
